import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {
    Alert,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    TextField,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import {swallowException} from "../../utils/appUtils";
import {getResourceErrorMessage, isResourceFailed} from "../../utils/resourceUtils";
import {useOrderManagement} from "../../hooks/ordersHooks";
import {OrderFlags} from "../../types/domain/orderFlags";
import {Order} from "../../types/domain/order";

export interface Props {
    companyId: string;
    order: Order;
    open: boolean;
    onClose: (isSuccessful: boolean) => void;
}

export const OrderFlagsEditDialog: React.FC<Props> = ({companyId, order, open, onClose}) => {
    const {t} = useTranslation();
    const orderManagement = useOrderManagement();
    const _flags = order.flags ? order.flags : [];
    const [flags, setFlags] = useState<OrderFlags[]>(_flags);
    const orderFlagsOptions: string[] = Object.values(OrderFlags);

    const handleAddFlag = (_event: React.SyntheticEvent, newValue: string[]) => {
        if (newValue) {
            setFlags(newValue as OrderFlags[]);
        } else {
            setFlags([]);
        }
    };

    const handleSubmit = () => {
        orderManagement.reset();

        orderManagement
            .changeFlags(companyId, order.id, flags)
            .then(() => {
                onClose(true);
            })
            .catch(swallowException);
    };

    return (
        <Dialog open={open} onClose={() => onClose(false)}>
            <DialogTitle>{t("dialogs.order_flags.title", {orderId: order.id.slice(0, 8)})}</DialogTitle>
            <DialogContent dividers>
                {isResourceFailed(orderManagement.resource) && (
                    <Alert severity="error" sx={{marginBottom: "1rem"}}>
                        {getResourceErrorMessage(orderManagement.resource)}
                    </Alert>
                )}
                <DialogContentText>
                    <Stack gap="1rem" minWidth="32rem" minHeight="20rem">
                        <Autocomplete
                            multiple
                            id="tags-autocomplete"
                            options={orderFlagsOptions}
                            value={flags}
                            onChange={handleAddFlag}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    label={t("dialogs.order_flags.add_flags")}
                                    placeholder="Flags"
                                />
                            )}
                        />
                        <Stack gap="0.5rem" justifyContent="start" flexWrap="wrap" direction="row">
                            {flags.map((option: string, index: number) => (
                                <Chip variant="outlined" label={option} key={index} />
                            ))}
                        </Stack>
                    </Stack>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button color="error" onClick={() => onClose(false)}>
                    {t("dialogs.actions.cancel")}
                </Button>
                <Button onClick={handleSubmit}>{t("dialogs.actions.confirm")}</Button>
            </DialogActions>
        </Dialog>
    );
};
